import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import SearchableSelect from '../../../../components/v2/SearchableSelect.jsx';
import SelectAllClearToggles from '../../../../components/v2/SelectAllClearToggles.jsx';

/**
 * Area dropdown presentation: true splits the list into "Available" /
 * "At max occupancy" group headers; false renders one flat list with full
 * rooms last, each tagged with a "Full" hint. Behavior (what is selectable,
 * what is searchable) is identical either way.
 */
const GROUP_AREAS_BY_AVAILABILITY = true;

/**
 * ResidentDrawer — slide-out drawer for Add / Edit resident form.
 * Keeps the table visible for context.
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   resident: object | null,    // null = Add mode, object = Edit mode
 *   onSave: (data: object) => void,
 *   areas: Array<{ area_id: number, area_name: string, area_type_name: string }>,
 *   groups: Array<{ resident_group_id: number, resident_group_name: string }>
 * }} props
 */
const ResidentDrawer = ({ isOpen, onClose, resident, onSave, areas, groups, offsiteActive, residents }) => {
  const isEditMode = !!resident;
  const drawerRef = useRef(null);

  // Compute current occupancy per area from active residents
  const areaOccupancy = useMemo(() => {
    const counts = {};
    (residents || []).forEach((r) => {
      if (r.is_active && r.area_id) {
        // In edit mode, exclude the current resident from the count
        if (isEditMode && r.resident_id === resident?.resident_id) return;
        counts[r.area_id] = (counts[r.area_id] || 0) + 1;
      }
    });
    return counts;
  }, [residents, isEditMode, resident]);

  // Areas for the dropdown: available rooms (A–Z) first, then full rooms
  // (A–Z, disabled). Sort is case-insensitive and numeric-aware so
  // "Room 2" comes before "Room 10". Full rooms stay in the list — and stay
  // searchable — so staff can see the room exists rather than assume it is
  // missing; they simply cannot be selected.
  const { areaOptions, areaGroups } = useMemo(() => {
    const byName = (a, b) =>
      (a.area_name || '').localeCompare(b.area_name || '', undefined, {
        sensitivity: 'base',
        numeric: true,
      });
    const available = [];
    const full = [];
    (areas || []).forEach((area) => {
      const occ = areaOccupancy[area.area_id] || 0;
      const max = parseInt(area.max_occupancy, 10) || 0;
      if (occ >= max) full.push(area); else available.push(area);
    });
    available.sort(byName);
    full.sort(byName);

    const toOption = (area, isFull) => ({
      value: area.area_id.toString(),
      label: `${area.area_name} (${area.area_type_name})`,
      hint: isFull ? 'Full' : undefined,
      isDisabled: isFull,
      // Search matches name + type only, so typing a room number or wing
      // finds it regardless of how the label or hint is composed.
      searchText: `${area.area_name || ''} ${area.area_type_name || ''}`,
    });

    return {
      areaOptions: [
        ...available.map((a) => toOption(a, false)),
        ...full.map((a) => toOption(a, true)),
      ],
      areaGroups: [
        ...(available.length
          ? [{ label: 'Available', options: available.map((a) => toOption(a, false)) }]
          : []),
        ...(full.length
          ? [{ label: 'At max occupancy', options: full.map((a) => toOption(a, true)) }]
          : []),
      ],
    };
  }, [areas, areaOccupancy]);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nickname, setNickname] = useState('');
  const [areaId, setAreaId] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [careLevel, setCareLevel] = useState('');
  const [status, setStatus] = useState('Active');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  // Populate form on open/resident change
  useEffect(() => {
    if (isOpen && resident) {
      setFirstName(resident.resident_first_name || '');
      setLastName(resident.resident_last_name || '');
      setNickname(resident.resident_nickname || '');
      setAreaId(resident.area_id?.toString() || '');
      setPhone(resident.resident_phone || '');
      setEmail(resident.resident_email || '');
      setCareLevel(resident.ehr_level_of_care || '');
      setStatus(resident.status || (resident.is_active ? 'Active' : 'Moved Out'));
      setSelectedGroups((resident.resident_groups || []).map((g) => g.resident_group_id));
    } else if (isOpen && !resident) {
      setFirstName('');
      setLastName('');
      setNickname('');
      setAreaId('');
      setPhone('');
      setEmail('');
      setCareLevel('');
      setStatus('Active');
      setSelectedGroups([]);
    }
  }, [isOpen, resident]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus trap — focus first input on open
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input, select');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleGroupToggle = useCallback((groupId) => {
    setSelectedGroups((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSaving(true);

      const selectedArea = areas.find((a) => a.area_id.toString() === areaId);

      const formData = {
        resident_id: resident?.resident_id || null,
        resident_first_name: firstName.trim(),
        resident_last_name: lastName.trim(),
        resident_nickname: nickname.trim(),
        area_id: parseInt(areaId, 10),
        area_name: selectedArea?.area_name || '',
        area_type_name: selectedArea?.area_type_name || '',
        area_type_description: selectedArea?.area_type_description || '',
        resident_phone: phone.trim(),
        resident_email: email.trim(),
        ehr_level_of_care: careLevel.trim(),
        status,
        is_active: status === 'Active',
        resident_groups: groups
          .filter((g) => selectedGroups.includes(g.resident_group_id))
          .map((g) => ({ resident_group_id: g.resident_group_id, resident_group_name: g.resident_group_name })),
        devices: resident?.devices || [],
      };

      try {
        await onSave(formData);
      } finally {
        setSaving(false);
      }
    },
    [firstName, lastName, nickname, areaId, phone, email, careLevel, status, selectedGroups, resident, areas, groups, onSave]
  );

  const isFormValid = firstName.trim() && lastName.trim() && areaId
    && (!offsiteActive || phone.trim());

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="rp-drawer-backdrop"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className={`rp-drawer rp-drawer--lg ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={isEditMode ? 'Edit Resident' : 'Add Resident'}
      >
        {/* Header */}
        <div className="rp-drawer__header">
          <h2 className="rp-drawer__title">
            {isEditMode ? 'Edit Resident' : 'Add Resident'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rp-drawer__close"
            aria-label="Close drawer"
          >
            <svg className="rp-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <form onSubmit={handleSubmit} className="rp-form">
          <div className="rp-form__body">
            {/* Area + name fields.
                data-help-id: help-guide anchor (docs/INTERACTIVE_HELP_PLAN.md
                §5.2). The move-in step's copy is "Choose the area, then add
                their name", so the ring encloses both rather than the Area field
                alone — pointing at one field while the instruction names two
                left the user hunting for the second. Phone is deliberately out,
                even when offsiteActive makes it required: it sits two blocks
                lower, so including it would drag Nickname inside the ring.

                `rp-form__stack` is load-bearing. `.rp-form__body > * + *` only
                reaches direct children, so without it this wrapper collapses the
                gap between Area and the name grid. */}
            <div className="rp-form__stack" data-help-id="resident-drawer.area">
              <div>
                <label htmlFor="drawer-area" className="rp-field__label">
                  Area <span className="rp-field__required">*</span>
                </label>
                <SearchableSelect
                  id="drawer-area"
                  options={GROUP_AREAS_BY_AVAILABILITY ? undefined : areaOptions}
                  groups={GROUP_AREAS_BY_AVAILABILITY ? areaGroups : undefined}
                  value={areaId}
                  onChange={(val) => setAreaId(val || '')}
                  placeholder="Search or select area..."
                  ariaLabel="Area"
                  required
                  isClearable
                />
              </div>

              {/* Name fields */}
              <div className="rp-form-grid">
                <div>
                  <label htmlFor="drawer-first-name" className="rp-field__label">
                    First Name <span className="rp-field__required">*</span>
                  </label>
                  <input
                    type="text"
                    id="drawer-first-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    pattern="^[0-9a-zA-Z_\- ']+$"
                    className="rp-field__input"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label htmlFor="drawer-last-name" className="rp-field__label">
                    Last Name <span className="rp-field__required">*</span>
                  </label>
                  <input
                    type="text"
                    id="drawer-last-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    pattern="^[0-9a-zA-Z_\- ']+$"
                    className="rp-field__input"
                    placeholder="Last name"
                  />
                </div>
              </div>
            </div>

            {/* Nickname */}
            <div>
              <label htmlFor="drawer-nickname" className="rp-field__label">Nickname</label>
              <input
                type="text"
                id="drawer-nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                className="rp-field__input"
                placeholder="Nickname (optional)"
              />
            </div>

            {/* Phone & Email */}
            <div className="rp-form-grid">
              <div>
                <label htmlFor="drawer-phone" className="rp-field__label">
                  Phone{offsiteActive ? <span className="rp-field__required"> *</span> : null}
                </label>
                <input
                  type="tel"
                  id="drawer-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required={offsiteActive}
                  pattern="[0-9]{3}-?[0-9]{3}-?[0-9]{4}"
                  className="rp-field__input"
                  placeholder="555-555-5555"
                />
              </div>
              <div>
                <label htmlFor="drawer-email" className="rp-field__label">Email</label>
                <input
                  type="email"
                  id="drawer-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rp-field__input"
                  placeholder="email@example.com"
                />
              </div>
            </div>

            {/* Status (edit mode only) */}
            {isEditMode && (
              <div>
                <label htmlFor="drawer-status" className="rp-field__label">Status</label>
                <select
                  id="drawer-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="rp-field__select"
                >
                  <option value="Active">Active</option>
                  <option value="Moved Out">Moved Out</option>
                  <option value="Pending Move-In">Pending Move-In</option>
                </select>
              </div>
            )}

            {/* Care Level */}
            <div>
              <label htmlFor="drawer-care-level" className="rp-field__label">Care Level</label>
              <input
                type="text"
                id="drawer-care-level"
                value={careLevel}
                onChange={(e) => setCareLevel(e.target.value)}
                className="rp-field__input"
                placeholder="e.g., Level 2"
              />
            </div>

            {/* Groups */}
            <div>
              <div className="sac-field-header">
                <p className="rp-field__label">Groups</p>
                {groups.length > 0 ? (
                  <SelectAllClearToggles
                    onSelectAll={() => setSelectedGroups(groups.map((g) => g.resident_group_id))}
                    onClear={() => setSelectedGroups([])}
                    allSelected={selectedGroups.length === groups.length}
                    noneSelected={selectedGroups.length === 0}
                  />
                ) : null}
              </div>
              <div className="rp-group-grid">
                {groups.map((group) => {
                  const isChecked = selectedGroups.includes(group.resident_group_id);
                  return (
                    <label
                      key={group.resident_group_id}
                      className={`rp-group-option ${isChecked ? 'is-selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleGroupToggle(group.resident_group_id)}
                        className="rp-checkbox"
                      />
                      <span className="rp-group-option__text">{group.resident_group_name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer with actions */}
          <div className="rp-form__footer">
            <button
              type="button"
              onClick={onClose}
              className="rp-btn rp-btn--cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid || saving}
              className="rp-btn rp-btn--primary"
              /* Help-guide anchor (docs/INTERACTIVE_HELP_PLAN.md §5.2) */
              data-help-id="resident-drawer.submit"
            >
              {saving ? (
                <span className="rp-saving-text">
                  <svg className="rp-icon-md rp-spinner" fill="none" viewBox="0 0 24 24">
                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Saving...
                </span>
              ) : (
                isEditMode ? 'Save Changes' : 'Add Resident'
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default ResidentDrawer;
